import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface InventoryBalance { _id: string; productId: any; variantId: any; locationId: any; quantityOnHand: number; quantityAvailable: number; averageCost: number; totalValue: number; }
export interface StockAlert { productId: string; name: string; sku: string; alertQuantity: number; currentStock: number; deficit: number; }
export interface StockTransfer { _id: string; transferNumber: string; date: string; sourceLocationId: any; destinationLocationId: any; status: string; totalQuantity: number; lines: any[]; createdBy: any; }
export interface StockAdjustment { _id: string; adjustmentNumber: string; date: string; locationId: any; adjustmentType: string; status: string; totalIncreaseQty: number; totalDecreaseQty: number; reason: string; lines: any[]; createdBy: any; reasonCode?: string; recoveredAmount?: number; attachments?: string[]; }
export interface InventoryMovement { _id: string; productId: any; movementType: string; quantityIn: number; quantityOut: number; unitCost: number; referenceNumber: string; narration: string; createdAt: string; createdBy: any; }

export function useBalances(filters?: { page?: number; limit?: number; productId?: string; locationId?: string }) {
  return useQuery({ queryKey: ['inventory', 'balances', filters], queryFn: async () => { const p = new URLSearchParams(); if (filters?.page) p.append('page', String(filters.page)); if (filters?.limit) p.append('limit', String(filters.limit)); if (filters?.productId) p.append('productId', filters.productId); if (filters?.locationId) p.append('locationId', filters.locationId); const { data } = await api.get(`/api/inventory/balances?${p}`); return data as { data: InventoryBalance[]; pagination: any }; } });
}

export function useAlerts() {
  return useQuery({ queryKey: ['inventory', 'alerts'], queryFn: async () => { const { data } = await api.get('/api/inventory/alerts'); return data as { alerts: StockAlert[]; total: number }; } });
}

export function useMovements(filters?: { page?: number; productId?: string }) {
  return useQuery({ queryKey: ['inventory', 'movements', filters], queryFn: async () => { const p = new URLSearchParams(); if (filters?.page) p.append('page', String(filters.page)); if (filters?.productId) p.append('productId', filters.productId); const { data } = await api.get(`/api/inventory/movements?${p}`); return data as { data: InventoryMovement[]; pagination: any }; } });
}

export function useTransfers(status?: string) {
  return useQuery({ queryKey: ['inventory', 'transfers', status], queryFn: async () => { const p = status ? `?status=${status}` : ''; const { data } = await api.get(`/api/inventory/transfers${p}`); return data as { data: StockTransfer[]; pagination: any }; } });
}

export function useAdjustments(status?: string) {
  return useQuery({ queryKey: ['inventory', 'adjustments', status], queryFn: async () => { const p = status ? `?status=${status}` : ''; const { data } = await api.get(`/api/inventory/adjustments${p}`); return data as { data: StockAdjustment[]; pagination: any }; } });
}

export interface BusinessLocation {
  id: string; _id: string; code: string; name: string; gstNumber: string;
  isActive: boolean; addressText: string;
  /** Hierarchy fields — always present in responses, defaulted for older rows. */
  facilityType: string;
  parentId: string | null;
  isVirtual: boolean;
  isInTransit: boolean;
  companyId: string;
}

/** Business locations (warehouses / stores) — required for stock transfers. */
export function useLocations(enabled = true) {
  return useQuery({
    queryKey: ['inventory', 'locations'],
    queryFn: async () => (await api.get<BusinessLocation[]>('/api/inventory/locations')).data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      code: string; name: string; city?: string; state?: string; gstNumber?: string;
      facilityType?: string; parentId?: string; isVirtual?: boolean; isInTransit?: boolean; companyId?: string;
    }) => (await api.post('/api/inventory/locations', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'locations'] }),
  });
}

export function useCreateTransfer() { const qc = useQueryClient(); return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/inventory/transfers', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'transfers'] }) }); }
export interface CreateAdjustmentPayload {
  locationId?: string;
  adjustmentType?: 'normal' | 'abnormal';
  /** Structured reason driving GL account selection. */
  reasonCode?: string;
  reason?: string;
  recoveredAmount?: number;
  notes?: string;
  attachments?: string[];
  lines: { productId: string; adjustedQuantity: number; unitCost?: number; reason?: string }[];
}

export function useCreateAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateAdjustmentPayload) =>
      (await api.post('/api/inventory/adjustments', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'adjustments'] }),
  });
}
export function useTransferAction() { const qc = useQueryClient(); return useMutation({ mutationFn: async ({ id, action }: { id: string; action: string }) => { const { data } = await api.post(`/api/inventory/transfers/${id}/${action}`); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }) }); }
export function useAdjustmentAction() { const qc = useQueryClient(); return useMutation({ mutationFn: async ({ id, action, body }: { id: string; action: string; body?: any }) => { const { data } = await api.post(`/api/inventory/adjustments/${id}/${action}`, body); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }) }); }


// ── Reorder PO from alert ────────────────────────────────────────────────────

export function useCreateReorderPO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (productId: string) => {
      const { data } = await api.post('/api/inventory/reorder-po', { productId });
      return data as { ok: boolean; purchaseOrder: any; reorderQty: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'alerts'] });
      qc.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Advanced inventory capabilities (in-transit, counts, bins, reorder, aging,
// traceability). These read the greenfield accounting inventory router, which
// uses the { ok, data, meta } envelope rather than the bare shapes above.
//
// Transfer MUTATIONS deliberately stay on useTransferAction() above: stock
// transfers have a single public contract at /api/inventory/transfers/*.
// ─────────────────────────────────────────────────────────────────────────────

const ACCOUNTING_INVENTORY = '/api/accounting/inventory';

/** Envelope returned by every /api/accounting/* endpoint. */
interface AccountingEnvelope<T> {
  ok: boolean;
  data: T;
}

async function getAccounting<T>(path: string, params?: Record<string, string>): Promise<T> {
  const { data } = await api.get<AccountingEnvelope<T>>(`${ACCOUNTING_INVENTORY}${path}`, { params });
  return data.data;
}

export interface InTransitLine {
  shippedQty: number;
  receivedQty?: number;
  damagedQty?: number;
  rejectedQty?: number;
}

export interface InTransitRecord {
  _id: string;
  transitNo: string;
  sourceType: string;
  sourceDocNo?: string;
  sourceId?: string;
  shippedAt?: string | null;
  expectedArrival?: string | null;
  carrier?: string;
  totalValueMinor: number;
  status: string;
  lines?: InTransitLine[];
}

export interface PhysicalCountRecord {
  _id: string;
  countNo: string;
  countType: string;
  snapshotDate?: string | null;
  snapshotData?: unknown[];
  totalVarianceQty?: number;
  totalVarianceValueMinor?: number;
  status: string;
}

export interface WarehouseBinRecord {
  _id: string;
  code: string;
  name?: string;
  binType?: string;
  zone?: string;
  aisle?: string;
  ownershipType?: string;
  temperatureControlled?: boolean;
  isActive?: boolean;
}

export interface ReorderSuggestion {
  productId: string;
  name: string;
  sku?: string;
  currentAvailable: number;
  reorderPoint: number;
  safetyStock: number;
  suggestedOrderQty: number;
  daysOfStockRemaining?: number | null;
  leadTimeDays: number;
  urgency: string;
}

export interface StockAnalysisItem {
  productId: string;
  locationId?: string | null;
  name: string;
  sku?: string;
  quantityOnHand: number;
  stockValueMinor: number;
  lastMovementAt?: string | null;
  daysSinceLastMovement?: number | null;
  totalMovedInPeriod?: number;
}

export interface SlowMovingResult {
  nonMoving: StockAnalysisItem[];
  slowMoving: StockAnalysisItem[];
  totalDeadStockValueMinor?: number;
  daysPeriod?: number;
}

export interface TraceEntry {
  movementId: string;
  date: string;
  movementType: string;
  direction?: string;
  referenceType?: string;
  referenceNumber?: string;
  quantityIn?: number;
  quantityOut?: number;
  quantity?: number;
  narration?: string;
}

export interface TraceResult {
  batchNumber?: string;
  serialNumber?: string;
  currentStatus?: string;
  upstream?: TraceEntry[];
  downstream?: TraceEntry[];
  lifecycle?: TraceEntry[];
  affectedDocuments?: TraceEntry[];
  totalQtyIssued?: number;
  summary?: { totalReceived: number; totalIssued: number; movementCount: number };
}

export function useInTransit(status?: string) {
  return useQuery({
    queryKey: ['inventory', 'transit', status],
    queryFn: () => getAccounting<{ items: InTransitRecord[]; total: number }>('/transit', status ? { status } : undefined),
  });
}

export function usePhysicalCounts(status?: string) {
  return useQuery({
    queryKey: ['inventory', 'counts', status],
    queryFn: () => getAccounting<{ items: PhysicalCountRecord[]; total: number }>('/counts', status ? { status } : undefined),
  });
}

export function useWarehouseBins(locationId?: string) {
  return useQuery({
    queryKey: ['inventory', 'bins', locationId],
    queryFn: () => getAccounting<{ items: WarehouseBinRecord[]; total: number }>('/bins', locationId ? { locationId } : undefined),
  });
}

export function useReorderSuggestions(locationId?: string) {
  return useQuery({
    queryKey: ['inventory', 'reorder', locationId],
    queryFn: () => getAccounting<{ suggestions: ReorderSuggestion[]; totalItems: number }>('/reorder', locationId ? { locationId } : undefined),
  });
}

export function useSlowMoving(daysPeriod = 90) {
  return useQuery({
    queryKey: ['inventory', 'slow-moving', daysPeriod],
    queryFn: () => getAccounting<SlowMovingResult>('/alerts/slow-moving', { daysPeriod: String(daysPeriod) }),
  });
}

export function useCreatePhysicalCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { countType: string; locationId: string; snapshotDate?: string; notes?: string }) =>
      (await api.post(`${ACCOUNTING_INVENTORY}/counts`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'counts'] }),
  });
}

/** Count lifecycle: freeze | submit | approve | post. */
export function useCountAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'freeze' | 'submit' | 'approve' | 'post' }) =>
      (await api.post(`${ACCOUNTING_INVENTORY}/counts/${id}/${action}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  });
}

export function useCreateWarehouseBin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      locationId: string; code: string; name?: string; binType?: string;
      zone?: string; aisle?: string; ownershipType?: string; temperatureControlled?: boolean;
    }) => (await api.post(`${ACCOUNTING_INVENTORY}/bins`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'bins'] }),
  });
}

/** Batch / serial / recall trace. Run on demand rather than on mount. */
export function useTrace() {
  return useMutation({
    mutationFn: async ({ kind, productId, batchNumber, serialNumber }: {
      kind: 'batch' | 'serial' | 'recall';
      productId: string;
      batchNumber?: string;
      serialNumber?: string;
    }) => {
      const params: Record<string, string> = { productId };
      if (kind === 'serial') params.serialNumber = serialNumber ?? '';
      else params.batchNumber = batchNumber ?? '';
      return getAccounting<TraceResult>(`/trace/${kind}`, params);
    },
  });
}
